const positiveId = value => Number.isInteger(Number(value)) && Number(value) > 0 ? Number(value) : null;

function employeeIdentity(value) {
    const employeeId = positiveId(value && value.employeeId);
    const userId = positiveId(value && value.userId);
    return employeeId ? {key: `employee:${employeeId}`, url: `/api/employees/${employeeId}`} :
        userId ? {key: `user:${userId}`, url: `/api/employees/by-user/${userId}`} : null;
}

function inspectorEmployee(details, fallback) {
    const value = details || {};
    if (value.contact_only === true) return value;
    const access = value.permissions && value.permissions.card_access || {can_open: false, view: {}, manage: {}};
    if (!access.can_open) return {display_name: fallback, card_access: access, can_open_card: false};
    const absence = value.current_absence || null;
    const characteristics = value.characteristics || [];
    return {...value, display_name: value.full_name || fallback,
        position: value.operational_position || null,
        department_name: value.operational_department && value.operational_department.name,
        priority: value.reliability_priority || null,
        contacts: access.view && access.view.contacts ? value.contact_methods || [] : [],
        characteristics: access.view && access.view.characteristics ? characteristics : [],
        characteristics_total: access.view && access.view.characteristics ? characteristics.length : 0,
        card_access: access, can_open_card: access.can_open === true,
        absence, is_absent: Boolean(absence),
        absence_label: absence && absence.presentation && (absence.presentation.label || absence.presentation.code)};
}

// This DTO must come from the permission-filtered account card, never a lookup/list row.
function contactOnlyEmployee(card, fallback) {
    const value = card || {};
    const contacts = (Array.isArray(value.contact_methods) ? value.contact_methods : [])
        .filter(contact => contact && ['phone', 'email'].includes(contact.contact_type) && contact.value)
        .map(contact => ({contact_type: contact.contact_type, value: String(contact.value)}));
    [['phone', value.phone], ['email', value.gmail]].forEach(([type, text]) => {
        if (text && !contacts.some(contact => contact.contact_type === type && contact.value === String(text))) {
            contacts.push({contact_type: type, value: String(text)});
        }
    });
    return {display_name: value.full_name || fallback, contact_only: true, contacts,
        telegram_url: /^https:\/\/(?:t\.me|telegram\.me)\//i.test(value.telegram_url || '') ? value.telegram_url : null,
        can_open_card: false, card_access: {can_open: false, view: {}, manage: {}}};
}

function createRequestEmployeePreview(get) {
    const state = {employee: null, target: null, loading: false, error: '', retryable: false, trigger: null};
    const pending = new Map();
    let version = 0;
    let generation = 0;
    function load(target) {
        const identity = employeeIdentity(target);
        if (!identity) return Promise.reject(new Error('Сотрудник не указан'));
        const userId = positiveId(target.userId);
        const key = `${identity.key}:account:${userId || ''}`;
        if (pending.has(key)) return pending.get(key);
        const epoch = generation;
        const request = Promise.resolve().then(() => get(identity.url, {ignoreModal: true}))
            .then(response => response.data).catch(error => {
                const status = Number(error && error.response && error.response.status);
                if (!userId || ![403, 404].includes(status) || epoch !== generation) throw error;
                return get(`/api/users/${userId}/card`, {ignoreModal: true})
                    .then(response => contactOnlyEmployee(response.data, target.name));
            }).finally(() => {
                if (epoch === generation && pending.get(key) === request) pending.delete(key);
            });
        pending.set(key, request);
        return request;
    }
    async function open(target, trigger) {
        if (!employeeIdentity(target)) return;
        const current = ++version;
        state.target = target;
        state.trigger = trigger || state.trigger;
        state.employee = inspectorEmployee(null, target.name || 'Сотрудник');
        state.loading = true;
        state.error = '';
        state.retryable = false;
        try {
            const details = await load(target);
            if (current === version) {
                state.employee = inspectorEmployee(details, target.name);
                if (details.contact_only) state.error = 'Кадровая карточка недоступна. Показаны разрешённые контакты.';
            }
        } catch (error) {
            if (current !== version) return;
            const status = Number(error && error.response && error.response.status);
            state.error = status === 403 ? 'Сведения сотрудника закрыты вашими правами доступа.' :
                status === 404 ? 'Связанная карточка сотрудника не найдена.' : 'Не удалось загрузить сведения сотрудника.';
            state.retryable = ![403, 404].includes(status);
        } finally {
            if (current === version) state.loading = false;
        }
    }
    function close() {
        version++;
        state.employee = null;
        state.target = null;
        state.loading = false;
    }
    function reset() { close(); generation++; pending.clear(); state.trigger = null; }
    return {state, load, open, close, reset};
}

module.exports = {employeeIdentity, inspectorEmployee, contactOnlyEmployee, createRequestEmployeePreview};
